import * as Sentry from '@sentry/react-native';
import { useEffect, useState } from 'react';

import { asApiError, PSErrorClass } from '@/src/api/apiError';
import bookingsApi, { type Booking } from '@/src/api/bookingsApi';

/**
 * usePollBooking — `GET /bookings/{id}`, asked again until the booking settles.
 *
 * ── Why a POLL and not a fetch ──────────────────────────────────────────
 * Nothing the client does confirms a booking. Razorpay's sheet closing means
 * only that the user finished with the sheet; what confirms the booking is the
 * `payment.captured` webhook Razorpay fires at our server afterwards, and that
 * can land before or after the user is back in the app. There's no callback and
 * no socket — the client's only way to learn the outcome is to keep asking (see
 * [[bookingsApi]]).
 *
 * So: `pending_payment` means "still waiting" and we ask again; ANY other status
 * is terminal and settles the hook. That includes the unhappy terminals
 * (`cancelled`, `expired`) — this hook's job is to wait for the booking to STOP
 * moving, not to judge where it stopped. The screen branches on
 * `booking.status`.
 *
 * A free booking arrives `confirmed` from `POST /bookings`, so the first ask
 * here settles immediately and the user sees the skeleton for one request.
 *
 * ── Which failures stop it ──────────────────────────────────────────────
 * A poll that dies on the first hiccup is worse than no poll — the user is
 * mid-payment on a phone network. So a transient failure (no connection, a 5xx,
 * an unreadable body) is just a tick that didn't land: we keep the loop going
 * and the user keeps seeing the skeleton.
 *
 * A 4xx is different — it's a verdict, not a hiccup. A 404 means this booking
 * isn't ours or doesn't exist and a 401 means the token is dead; asking again
 * gets the same answer forever, so those settle the hook as an error at once.
 *
 * ── Why it gives up ─────────────────────────────────────────────────────
 * `MAX_ATTEMPTS` bounds the loop. A capture that hasn't landed in a minute isn't
 * going to be noticed by a spinner, and an unbounded poll is a screen that
 * quietly burns battery and data until the user kills the app. Running out
 * settles as an error too — a different message, because "we're still waiting on
 * the bank" is not "something broke".
 */

/** How long between asks. */
const POLL_INTERVAL_MS = 2000;
/** How many asks before we give up. 30 × 2s ≈ a minute of waiting. */
const MAX_ATTEMPTS = 30;

const TIMEOUT_MESSAGE =
  "This is taking longer than usual to confirm. You'll find the booking under your tickets once it's through.";

/**
 * `loading` covers BOTH "the first request is in the air" and "we've asked and
 * it's still pending_payment" — from the screen's side they're the same wait, so
 * splitting them would only ask it to render two identical skeletons.
 *
 * `success` means SETTLED, not confirmed: the booking left `pending_payment`.
 * Read `booking.status` for which way it went.
 */
export type PollBookingState =
  | { status: 'loading'; booking: null; error: null }
  | { status: 'error'; booking: null; error: PSErrorClass }
  | { status: 'success'; booking: Booking; error: null };

/**
 * A settled result tagged with the `id` it belongs to — the same shape
 * [[useFetchEvent]] uses, and for the same reason: the public state is DERIVED
 * from it, so a result for a stale id reads as `loading` without the effect
 * having to synchronously reset state on an id change.
 */
type SettledResult =
  | { status: 'error'; booking: null; error: PSErrorClass; id: string }
  | { status: 'success'; booking: Booking; error: null; id: string };

const LOADING: PollBookingState = {
  status: 'loading',
  booking: null,
  error: null,
};

/**
 * Polls the booking for `id` until it settles. Starts in `loading` and stays
 * there for as long as the booking is `pending_payment`.
 *
 * The loop is a chain of timeouts rather than an interval, so a slow response
 * can't stack requests on top of each other — the next ask is scheduled only
 * once the previous one has come back.
 *
 * `active` is checked after every await: it's what drops a response that arrived
 * for an unmounted screen, or for a booking the user has already navigated away
 * from, and the cleanup also clears the pending timer so the chain ends there.
 */
export function usePollBooking(id: string): PollBookingState {
  const [result, setResult] = useState<SettledResult | null>(null);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    // Counted apart from `attempts` so a timeout can say WHICH wait it was: 30
    // pending reads is a capture that never landed, 30 failed requests is an
    // outage, and they send you to different places.
    let failedTicks = 0;

    const ask = async () => {
      attempts += 1;

      try {
        const booking = await bookingsApi.getBooking(id);
        if (!active) return;

        if (booking.status !== 'pending_payment') {
          setResult({ status: 'success', booking, error: null, id });
          return;
        }
        // Still pending — fall through and schedule the next ask.
      } catch (caught) {
        if (!active) return;

        const error = asApiError(caught);
        // A verdict, not a hiccup: asking again gets the same answer. Anything
        // else (network, 5xx, unreadable) falls through and is retried.
        if (error.kind === 'api') {
          setResult({ status: 'error', booking: null, error, id });
          return;
        }
        failedTicks += 1;
      }

      if (attempts >= MAX_ATTEMPTS) {
        // Nothing is broken for the USER — the copy below sends them to their
        // tickets and the booking may still confirm. But a capture that hasn't
        // reached our server in a minute is the one payment failure no screen,
        // no `catch` and no HTTP status reports, so the rate of this is the only
        // way we'd find out that confirmations have stopped arriving.
        Sentry.captureMessage('Booking poll timed out', {
          level: 'warning',
          extra: { bookingId: id, attempts, failedTicks },
        });
        setResult({
          status: 'error',
          booking: null,
          // 'unknown' rather than a kind of its own: nothing has reported a
          // failure — we simply stopped waiting — and the copy is ours, not a
          // server's.
          error: new PSErrorClass({
            kind: 'unknown',
            message: TIMEOUT_MESSAGE,
          }),
          id,
        });
        return;
      }

      timer = setTimeout(ask, POLL_INTERVAL_MS);
    };

    ask();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [id]);

  // Derived: loading until a result for THIS id has settled.
  return result && result.id === id ? result : LOADING;
}
